/**
 * Slice C.4 · ExpenseReceipt.
 * ---------------------------
 * Reads canonical `presentation.data` mirrored from the domain
 * authority `domain.instructor_expenses.create_instructor_expense`.
 *
 * Canonical fields (server → visible):
 *   • date          → "Date"      (ISO date, raw)
 *   • category      → "Category"  (canonical string, display-formatted:
 *                                   underscores → spaces, sentence case)
 *   • amount_pence  → "Amount"    (formatted £N.NN in the DOM;
 *                                  payload remains integer pence)
 *   • id            → present in payload only (NOT visibly rendered;
 *                     calm-interface doctrine per L.3).
 *
 * The renderer NEVER invents:
 *   • net / tax / VAT / merchant / payment_method;
 *   • receipt_url / notes / provisional / captured_at / provenance;
 *   • navigation, undo or confirmation action.
 *
 * Emitted on both `logged` and `replay` — narration distinguishes.
 * `presentation.data.id` is retained in the payload for future
 * reconciliation but NOT shown as a chip / abbreviated ID / copy
 * control.
 */
const _CATEGORY_LABELS = {
  fuel:             "Fuel",
  repairs:          "Repairs",
  insurance:        "Insurance",
  subscriptions:    "Subscriptions",
  cpd:              "CPD",
  parking:          "Parking",
  vehicle_finance:  "Vehicle finance",
  other:            "Other",
};

function _formatCategoryLabel(category) {
  if (typeof category !== "string" || category.length === 0) return null;
  if (Object.prototype.hasOwnProperty.call(_CATEGORY_LABELS, category)) {
    return _CATEGORY_LABELS[category];
  }
  // Fallback (defensive): underscores → spaces, sentence case.
  const spaced = category.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function _formatPence(pence) {
  if (!Number.isInteger(pence)) return null;
  return `£${(pence / 100).toFixed(2)}`;
}

export function ExpenseReceipt({ presentation }) {
  const d = presentation.data || {};
  const amountString      = _formatPence(d.amount_pence);
  const categoryRaw       = typeof d.category === "string" ? d.category : null;
  const categoryLabel     = _formatCategoryLabel(categoryRaw);
  const dateStr           = typeof d.date === "string" ? d.date : null;

  return (
    <div data-testid="ui-response-expense-receipt" className="space-y-3">
      <p
        className="text-[14px] leading-relaxed text-zinc-100"
        data-testid="ui-expense-receipt-narration"
      >
        {presentation.narration}
      </p>
      {(amountString || categoryLabel || dateStr) && (
        <dl className="text-[13px] grid grid-cols-2 gap-x-3 gap-y-1 text-zinc-300">
          {amountString && (
            <>
              <dt className="text-zinc-500">Amount</dt>
              <dd data-testid="ui-expense-receipt-amount">{amountString}</dd>
            </>
          )}
          {categoryLabel && (
            <>
              <dt className="text-zinc-500">Category</dt>
              <dd data-testid="ui-expense-receipt-category">{categoryLabel}</dd>
            </>
          )}
          {dateStr && (
            <>
              <dt className="text-zinc-500">Date</dt>
              <dd data-testid="ui-expense-receipt-date">{dateStr}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

export default ExpenseReceipt;
